import { rudderIdentify, rudderTrack, rudderTrackGroup } from './init';
import DineroFactory, { Currency } from 'dinero.js';

interface SignedUpEvent {
  email?: string;
  phone_number?: string;
  signup_source?: string;
}

export const signedUpEvent = async ({
  email,
  phone_number,
  signup_source,
}: SignedUpEvent) => {
  await rudderTrack('Signed Up', {
    email,
    phone_number,
    signup_source,
  });
};

interface SignedInEvent {
  email?: string;
  phone_number?: string;
  signin_source?: string;
}

export const signedInEvent = async ({
  email,
  phone_number,
  signin_source,
}: SignedInEvent) => {
  await rudderTrack('Signed In', {
    email,
    phone_number,
    signin_source,
  });
};

interface MerchReleaseInitiatedEvent {
  artist_id?: string;
  artist_name?: string;
}

export const merchReleaseInitiatedEvent = async ({
  artist_id,
  artist_name,
}: MerchReleaseInitiatedEvent) => {
  await rudderTrack('Merch Release Initiated', {
    artist_id,
    artist_name,
  });
};

interface MerchReleaseStartedEvent {
  artist_id?: string;
  artist_name?: string;
  product_type?: string;
}

export const merchReleaseStartedEvent = async ({
  artist_id,
  artist_name,
  product_type,
}: MerchReleaseStartedEvent) => {
  await rudderTrack('Merch Release Started', {
    artist_id,
    artist_name,
    product_type,
  });
};

interface MerchReleaseStepViewedEvent {
  step?: number;
  name?: string;
  artist_id?: string;
  artist_name?: string;
  is_edit?: boolean;
  product_id?: string;
  product_type?: number;
  created_by?: number | string;
  currency?: string;
  products?: unknown[];
}

export const merchReleaseStepViewedEvent = async ({
  step,
  name,
  artist_id,
  artist_name,
  is_edit,
  product_id,
  product_type,
  created_by,
  currency,
  products,
}: MerchReleaseStepViewedEvent) => {
  await rudderTrack('Merch Release Step Viewed', {
    step,
    name,
    artist_id,
    artist_name,
    is_edit,
    product_id,
    product_type,
    created_by,
    currency,
    products,
  });
};

interface MerchReleaseStepCompletedEvent {
  step?: number;
  name?: string;
  artist_id?: string;
  artist_name?: string;
  is_edit?: boolean;
  product_id?: string;
  product_type?: number;
  sizes?: string[];
  colors?: string[];
  shipping?: string | null;
}

export const merchReleaseStepCompletedEvent = async ({
  step,
  name,
  artist_id,
  artist_name,
  is_edit,
  product_id,
  product_type,
  sizes,
  colors,
}: MerchReleaseStepCompletedEvent) => {
  await rudderTrack('Merch Release Step Completed', {
    step,
    name,
    artist_id,
    artist_name,
    is_edit,
    product_id,
    product_type,
    sizes,
    colors,
  });
};

interface MerchCheckoutStarted {
  artist_id?: string;
  artist_name?: string;
  user_id?: string;
  user_name?: string;
  checkout_source?: string;
  created_by?: string;
  currency?: string;
  revenue?: string | number;
  products?: unknown[];
}

export const checkoutStarted = async ({
  artist_id,
  artist_name,
  user_id,
  user_name,
  checkout_source,
  created_by,
  currency,
  revenue,
  products,
}: MerchCheckoutStarted) => {
  await rudderTrack('Checkout Started', {
    artist_id,
    artist_name,
    user_id,
    user_name,
    checkout_source,
    created_by,
    currency,
    revenue,
    products,
  });
};

export interface MerchProductOrderItem {
  category?: 'Merch';
  purchase_type?: string;
  length?: string | number;
  product_id?: string;
  variant_id?: number;
  color?: string;
  size?: string;
  item_type?: string;
  quantity?: number;
  position?: number;
  price?: string | number;
  currency?: string;
  tax?: string;
}

export interface NFTProductOrderItem {
  category: 'NFT';
  type?: string;
  length?: string;
  product_id?: string;
  variant_id?: string;
  item_type?: string;
  color?: string;
  size?: string;
  quantity?: number;
  position?: number;
  price?: string;
  currency?: string;
  tax?: string;
}

interface OrderCompleted {
  created_by?: string;
  checkout_id?: string;
  checkout_source?: string;
  order_id?: string;
  artist_id?: string;
  artist_name?: string;
  artist_tracpage_url?: string;
  total_price?: string | number;
  total_quantity?: number;
  currency?: string;
  shipping_cost?: string | number;
  shipping_option?: 'express' | 'standard';
  total_tax?: string | number;
  products?:
    | MerchProductOrderItem[]
    | NFTProductOrderItem[]
    | [Record<string, string | number | undefined>];
  coupon?: string;
}

export const orderCompleted = async ({
  created_by,
  checkout_id,
  order_id,
  artist_id,
  artist_name,
  artist_tracpage_url,
  total_price,
  currency,
  shipping_cost,
  total_tax,
  products,
  coupon,
}: OrderCompleted) => {
  await rudderTrack('Order Completed', {
    checkout_id,
    order_id,
    total: DineroFactory({
      currency: (currency || 'USD') as Currency,
      amount: Number(total_price) || 0,
    }).toUnit(),
    shipping: shipping_cost,
    tax: total_tax || 0,
    coupon,
    currency,
    artist_tracpage_url,
    artist_id,
    created_by,
    artist_name,
    products,
  });
};

interface MerchReleaseCompletedEvent {
  artist_id?: string;
  artist_name?: string;
  is_edit?: boolean;
  product_id?: string;
  product_name?: string;
  product_type?: number;
  suggested_price?: number;
  currency?: string;
  unlimited?: boolean;
}

export const merchReleaseCompletedEvent = async ({
  artist_id,
  artist_name,
  is_edit,
  product_id,
  product_type,
  suggested_price,
  currency,
  unlimited,
}: MerchReleaseCompletedEvent) => {
  await rudderTrack('Merch Release Completed', {
    artist_id,
    artist_name,
    is_edit,
    product_id,
    product_type,
    suggested_price,
    currency,
    unlimited,
  });
};

interface PlanUpgradedEvent {
  from?: string;
  to: string;
}

export const planUpgradedEvent = async ({ from, to }: PlanUpgradedEvent) => {
  await rudderTrack('Plan Upgrade', {
    from,
    to,
  });
};

interface PlanDowngradedEvent {
  from?: string;
  to: string;
}

export const planDowngradedEvent = async ({
  from,
  to,
}: PlanDowngradedEvent) => {
  await rudderTrack('Plan Downgrade', {
    from,
    to,
  });
};

interface StreamingProfiles {
  name: string;
  profile_url?: string;
}

interface SocialProfiles {
  name: string;
  profile_url?: string;
}

interface ArtistProfileCompletedEvent {
  artist_id: string;
  artist_name?: string;
  tracpage_domain?: string;
  custom_domain?: string;
  email?: string;
  telephone?: string;
  streaming_profiles?: StreamingProfiles[];
  social_profiles: SocialProfiles[];
}

export const artistProfileCompleted = async ({
  artist_id,
  artist_name,
  streaming_profiles,
  social_profiles,
}: ArtistProfileCompletedEvent) => {
  await rudderTrackGroup(artist_id, {
    type: 'Artist Profile Completed (Group)',
    artist_id,
    artist_name,
    streaming_profiles,
    social_profiles,
  });
};

export const artistProfileUpdated = async ({
  artist_id,
  artist_name,
  tracpage_domain,
  custom_domain,
  email,
  telephone,
  streaming_profiles,
  social_profiles,
}: ArtistProfileCompletedEvent) => {
  await rudderTrackGroup(artist_id, {
    type: 'Artist Settings Updated (Group)',
    artist_id,
    artist_name,
    tracpage_domain,
    custom_domain,
    email,
    telephone,
    streaming_profiles,
    social_profiles,
  });
};

interface TracPageUpdatedEvent {
  artist_id: string;
  artist_name?: string;
  custom_background?: boolean;
  theme?: string;
  integrations?: {
    id?: string;
    name?: string;
    value?: string;
  }[];
  links_headers?: {
    url?: string;
    type?: string;
    title?: string;
  }[];
}

export const tracPageUpdated = async ({
  artist_id,
  artist_name,
  custom_background,
  theme,
  integrations,
  links_headers,
}: TracPageUpdatedEvent) => {
  await rudderTrackGroup(artist_id, {
    type: 'tracPage Updated (Group)',
    artist_id,
    artist_name,
    custom_background,
    theme,
    integrations,
    links_headers,
  });
};

export interface UserProfileUpdatedEvent {
  userId: string;
  full_name?: string;
  country?: string;
  state?: string;
  city?: string;
  zip?: string;
  email_addresses?: {
    email?: string;
    is_primary?: boolean;
  }[];
  phone_numbers?: {
    phone_number?: string;
    country_code?: string;
    country_name?: string;
  }[];
  address_line1?: string;
  address_line2?: string;
}

export const userSettingsUpdated = async ({
  userId,
  full_name,
  country,
  state,
  city,
  zip,
  email_addresses,
  phone_numbers,
  address_line1,
  address_line2,
}: UserProfileUpdatedEvent) => {
  await rudderIdentify(userId, {
    type: 'User Settings Updated (Identify)',
    full_name,
    country,
    state,
    city,
    zip,
    email_addresses,
    phone_numbers,
    address_line1,
    address_line2,
  });
};

export interface TeamMemberUpdatedEvent {
  artist_id?: string;
  role?: string;
  email_address?: string;
  phone_number?: string;
}

export const teamMemberAdded = async ({
  artist_id,
  role,
  email_address,
  phone_number,
}: TeamMemberUpdatedEvent) => {
  await rudderTrack('Team Member Added', {
    artist_id,
    role,
    email_address,
    phone_number,
  });
};

// TODO Add event to TeamsListCtrl->ArtistTeamItem
// export const teamMemberRemoved = async ({
//   artist_id,
//   role,
//   email_address,
//   phone_number
// }: TeamMemberUpdatedEvent) => {
//   await rudderTrack('Team Member Removed', {
//     artist_id,
//     role,
//     email_address,
//     phone_number
//   });
// };

interface ArtistDeletedEvent {
  artist_id: string;
  artist_name?: string;
  tracpage_domain?: string;
  custom_domain?: string;
  email?: string;
  telephone?: string;
  streaming_profiles?: StreamingProfiles[];
  social_profiles: SocialProfiles[];
  artist_deleted?: boolean;
}

export const artistDeletedEvent = async ({
  artist_id,
  artist_name,
  tracpage_domain,
  custom_domain,
  streaming_profiles,
  social_profiles,
  artist_deleted,
}: ArtistDeletedEvent) => {
  await rudderTrackGroup(artist_id, {
    type: 'Artist Deleted (Group)',
    artist_id,
    artist_name,
    tracpage_domain,
    custom_domain,
    streaming_profiles,
    social_profiles,
    artist_deleted,
  });
};

interface ReleasePresetsUpdatedEvent {
  artist_id: string;
  artist_name?: string;
  copyright?: string;
  price_per_single?: string;
  release_time: {
    hour?: number;
    minute?: number;
    period?: string;
  }[];
  label_id?: string;
  label_name?: string;
  genres: {
    name?: string;
    is_primary?: boolean;
  }[];
  language?: string;
}

export const releasePresetsUpdatedEvent = async ({
  artist_id,
  artist_name,
  copyright,
  price_per_single,
  release_time,
  label_id,
  label_name,
  genres,
  language,
}: ReleasePresetsUpdatedEvent) => {
  await rudderTrackGroup(artist_id, {
    type: 'Release Presets Updated (Group)',
    artist_id,
    artist_name,
    copyright,
    price_per_single,
    release_time,
    label_id,
    label_name,
    genres,
    language,
  });
};

export const withdrawalInitiated = async () => {
  await rudderTrack('Withdrawal Initiated', {});
};

export interface WithdrawalStartedEvent {
  amount?: string | number;
  currency?: string;
  account_name?: string;
  account_type?: string;
  withdraw_id?: string;
  withdraw_type?: string;
  account_id?: string;
}

export const withdrawalStarted = async ({
  amount,
  currency,
  account_name,
  account_type,
  withdraw_id,
  withdraw_type,
  account_id,
}: WithdrawalStartedEvent) => {
  await rudderTrack('Withdrawal Started', {
    amount,
    currency,
    account_name,
    account_type,
    withdraw_id,
    withdraw_type,
    account_id,
  });
};

export interface WithdrawalStepCompletedEvent {
  amount?: string | number;
  currency?: string;
  account_name?: string;
  account_type?: string;
  withdraw_id?: string;
  withdraw_type?: string;
  step?: number;
  name?: string;
  account_id?: string | number;
}

export const withdrawalStepCompleted = async ({
  amount,
  currency,
  account_name,
  account_type,
  withdraw_id,
  withdraw_type,
  step,
  name,
  account_id,
}: WithdrawalStepCompletedEvent) => {
  await rudderTrack('Withdrawal Step Completed', {
    amount,
    currency,
    account_name,
    account_type,
    withdraw_id,
    withdraw_type,
    step,
    name,
    account_id,
  });
};

export interface WithdrawalFailedEvent {
  amount?: string | number;
  currency?: string;
  account_name?: string;
  account_type?: string;
  withdraw_id?: string;
  withdraw_type?: string;
  account_id?: string;
  error?: string;
}

export const withdrawalFailed = async ({
  amount,
  currency,
  account_name,
  account_type,
  withdraw_id,
  withdraw_type,
  account_id,
  error,
}: WithdrawalFailedEvent) => {
  await rudderTrack('Withdrawal Failed', {
    amount,
    currency,
    account_name,
    account_type,
    withdraw_id,
    withdraw_type,
    account_id,
    error,
  });
};

export interface WithdrawalCompletedEvent {
  amount?: string | number;
  currency?: string;
  account_name?: string;
  account_type?: string;
  withdraw_id?: string;
  withdraw_type?: string;
  account_id?: string;
}

export const withdrawalCompleted = async ({
  amount,
  currency,
  account_name,
  account_type,
  withdraw_id,
  withdraw_type,
  account_id,
}: WithdrawalCompletedEvent) => {
  await rudderTrack('Withdrawal Completed', {
    amount,
    currency,
    account_name,
    account_type,
    withdraw_id,
    withdraw_type,
    account_id,
  });
};

export const paymentAccountInitiated = async () => {
  await rudderTrack('Payment Account Initiated', {});
};

export interface PaymentAccountStartedEvent {
  wallet_type: string;
}

export const paymentAccountStarted = async ({
  wallet_type,
}: PaymentAccountStartedEvent) => {
  await rudderTrack('Payment Account Started', {
    wallet_type,
  });
};

export interface PaymentAccountStepCompletedEvent {
  step: number | string;
  name: string;
}

export const paymentAccountStepCompleted = async ({
  step,
  name,
}: PaymentAccountStepCompletedEvent) => {
  await rudderTrack('Payment Account Step Completed', {
    step,
    name,
  });
};

export interface PaymentAccountCompletedEvent {
  account_id?: string;
  account_type?: string;
  account_name?: string;
  wallet_type: string;
}

export const paymentAccountCompleted = async ({
  account_id,
  account_type,
  account_name,
  wallet_type,
}: PaymentAccountCompletedEvent) => {
  await rudderTrack('Payment Account Completed', {
    account_id,
    account_type,
    account_name,
    wallet_type,
  });
};

export interface PaymentAccountRemovedEvent {
  account_id: string;
  account_type?: string;
  account_name?: string;
  wallet_type: string;
}

export const paymentAccountRemoved = async ({
  account_id,
  account_type,
  account_name,
  wallet_type,
}: PaymentAccountRemovedEvent) => {
  await rudderTrack('Payment Account Removed', {
    account_id,
    account_type,
    account_name,
    wallet_type,
  });
};
